import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { copyToClipboard } from '../utils/clipboard';

/* ===========================================================================
 * GenerateCode V2 — UX completo, sidebar sticky, presets rápidos,
 * cards visuais de rota, empty state, dark mode, mobile responsivo.
 * Regras de negócio inalteradas: BR / CN / INT, qty 1-100.
 * ========================================================================= */

const ROUTES = [
  {
    id: 'BR',
    label: 'BR',
    title: 'Nacional',
    subtitle: 'Brasil → Brasil',
    flag: '🇧🇷',
    color: '#22c55e',
    description: 'Rastreamento nacional, entrega via Correios.',
  },
  {
    id: 'CN',
    label: 'CN',
    title: 'Importação',
    subtitle: 'China → Brasil',
    flag: '🇨🇳',
    color: '#ef4444',
    description: 'Trajeto completo com etapas internacionais.',
  },
  {
    id: 'INT',
    label: 'INT',
    title: 'Internacional',
    subtitle: 'Outros países',
    flag: '🌐',
    color: '#3b82f6',
    description: 'Rota internacional genérica.',
  },
];

const QTY_PRESETS = [1, 5, 10, 25, 50, 100];

const dateOffsetISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const formatDateBR = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const GenerateCode = () => {
  const navigate = useNavigate();
  const { user, updateUserCredits, refreshUser } = useAuth();

  /* ---------- form state ---------- */
  const [origin, setOrigin] = useState('');
  const [routeType, setRouteType] = useState('BR');
  const [destinations, setDestinations] = useState('');
  const [quantity, setQuantity] = useState('');
  const [postDate, setPostDate] = useState('');

  /* ---------- ui state ---------- */
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generatedCode, setGeneratedCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pasted, setPasted] = useState(false);
  const [toast, setToast] = useState(null); // {type, msg}

  const destinationsRef = useRef(null);
  const resultRef = useRef(null);

  /* ---------- auto-refresh credits on focus ---------- */
  useEffect(() => {
    refreshUser();
    const onFocus = () => refreshUser();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- destinations parsing ---------- */
  const destinationList = useMemo(() => {
    return destinations
      .split('\n')
      .map((d) => d.trim())
      .filter((d) => d.length > 0);
  }, [destinations]);

  const destinationCount = destinationList.length;

  /* ---------- cost calc ---------- */
  const effectiveQty = destinationCount > 1 ? destinationCount : parseInt(quantity) || 0;
  const currentCredits = user?.credits ?? 0;
  const remainingCredits = currentCredits - effectiveQty;
  const hasEnoughCredits = effectiveQty > 0 && remainingCredits >= 0;
  const noCredits = currentCredits === 0;

  /* ---------- toast helper ---------- */
  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  }, []);

  /* ---------- validate ---------- */
  const validate = useCallback(() => {
    const e = {};
    if (!routeType) e.routeType = 'Selecione um tipo de rota';

    if (destinationCount > 999) e.destinations = 'Máximo de 999 destinos por geração';

    if (destinationCount <= 1) {
      // se 0 ou 1 destino, precisa de quantity válido
      if (quantity === '' || quantity === null || quantity === undefined) {
        e.quantity = 'Informe a quantidade (1 a 100)';
      } else {
        const n = Number(quantity);
        if (!Number.isInteger(n) || n < 1 || n > 100) {
          e.quantity = 'Quantidade deve ser inteiro entre 1 e 100';
        }
      }
    }

    if (origin && origin.trim().length > 0 && origin.trim().length < 3) {
      e.origin = 'Origem deve ter no mínimo 3 caracteres';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }, [routeType, destinationCount, quantity, origin]);

  /* ---------- handlers ---------- */
  const handleDestChange = (val) => {
    setDestinations(val);
    if (errors.destinations) setErrors((prev) => ({ ...prev, destinations: '' }));
  };

  const handlePasteDestinations = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) {
        showToast('warn', 'Clipboard vazio');
        return;
      }
      // normaliza separadores: vírgula, ponto-e-vírgula, tab → quebra de linha
      const normalized = text
        .replace(/[\t;]/g, '\n')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);

      // se não houver linhas E texto tiver vírgulas, separa por vírgula
      const lines =
        normalized.length === 1 && text.includes(',')
          ? text.split(',').map((s) => s.trim()).filter(Boolean)
          : normalized;

      const truncated = lines.slice(0, 999).join('\n');
      setDestinations(truncated);
      setPasted(true);
      setTimeout(() => setPasted(false), 2000);
      showToast('success', `${Math.min(lines.length, 999)} destinos colados${lines.length > 999 ? ' (truncado em 999)' : ''}`);
    } catch (err) {
      showToast('warn', 'Sem permissão para acessar clipboard');
    }
  };

  const clearDestinations = () => {
    setDestinations('');
    if (errors.destinations) setErrors((prev) => ({ ...prev, destinations: '' }));
  };

  const setQuantityPreset = (n) => {
    if (destinationCount > 1) {
      showToast('warn', 'Quantidade definida pelos destinos');
      return;
    }
    setQuantity(n);
    if (errors.quantity) setErrors((prev) => ({ ...prev, quantity: '' }));
  };

  const setDatePreset = (offset) => {
    setPostDate(dateOffsetISO(offset));
  };

  /* ---------- submit ---------- */
  const handleSubmit = (e) => {
    e?.preventDefault?.();
    setError('');
    setSuccess('');
    setCopied(false);

    if (noCredits) {
      showToast('warn', 'Você precisa comprar créditos antes');
      return;
    }
    if (!validate()) {
      // foca primeiro campo inválido
      return;
    }
    if (!hasEnoughCredits) {
      showToast('warn', `Faltam ${Math.abs(remainingCredits)} créditos`);
      return;
    }
    setShowConfirm(true);
  };

  const doGenerate = async () => {
    setShowConfirm(false);
    setGeneratedCode(null);
    setLoading(true);

    const payload = {
      origin: origin.trim() || undefined,
      routeType,
      destinations: destinations.trim() || undefined,
      quantity: destinationCount > 1 ? destinationCount : parseInt(quantity) || 1,
      postDate: postDate || undefined,
    };

    try {
      const response = await api.post('/tracking/generate', payload);
      setGeneratedCode(response.data);
      setSuccess(`${response.data.resultados.length} código(s) gerado(s) com sucesso!`);
      updateUserCredits(response.data.remainingCredits);

      // reset form (mantém routeType pra agilizar próximas gerações)
      setOrigin('');
      setDestinations('');
      setQuantity('');
      setPostDate('');

      // scroll suave até o resultado
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao gerar código';
      setError(msg);
      showToast('error', msg);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- result helpers ---------- */
  const copyAllCodes = async () => {
    if (!generatedCode) return;
    const txt = generatedCode.resultados
      .map((r) => (r.destino && r.destino !== 'Não especificado' ? `${r.codigo}: ${r.destino}` : `${r.codigo}:`))
      .join('\n');
    const ok = await copyToClipboard(txt);
    if (ok) {
      setCopied(true);
      showToast('success', 'Todos os códigos copiados');
      setTimeout(() => setCopied(false), 2000);
    } else {
      showToast('error', 'Não foi possível copiar');
    }
  };

  const copySingle = async (code) => {
    const ok = await copyToClipboard(code);
    showToast(ok ? 'success' : 'error', ok ? `${code} copiado` : 'Não foi possível copiar');
  };

  const downloadCSV = () => {
    if (!generatedCode) return;
    const header = 'codigo,destino,rota,origem\n';
    const rows = generatedCode.resultados
      .map((r) => `${r.codigo},"${r.destino || ''}",${generatedCode.routeType},"${generatedCode.origin || ''}"`)
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `codigos_${generatedCode.routeType}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ---------- keyboard shortcut Ctrl+Enter ---------- */
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !loading && !showConfirm) {
        handleSubmit(e);
      }
      if (e.key === 'Escape' && showConfirm) setShowConfirm(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, showConfirm, routeType, origin, destinations, quantity, postDate]);

  /* ---------- preview do código (visual) ---------- */
  const codePreview = useMemo(() => {
    return `${routeType}-XXXXX-X`;
  }, [routeType]);

  /* ---------- compute progress bar % ---------- */
  const destPct = Math.min((destinationCount / 999) * 100, 100);

  /* ===================================================================== */
  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{styles}</style>

      <div className="gc">
        {/* ====== HEADER STICKY com saldo ====== */}
        <header className="gc-header">
          <div className="gc-header-inner">
            <div className="gc-header-left">
              {/* Botão "Voltar" removido — navegação já fica na sidebar (ClientLayout) */}
              <div>
                <h1 className="gc-header-title">Gerar Códigos</h1>
                <p className="gc-header-sub">Rastreamento BR · CN · INT</p>
              </div>
            </div>
            <div className="gc-header-right">
              <div className={`gc-balance-chip ${noCredits ? 'empty' : currentCredits < 10 ? 'low' : ''}`}>
                <i className="fas fa-coins"></i>
                <div className="gc-balance-info">
                  <span className="gc-balance-label">Seus créditos</span>
                  <span className="gc-balance-val">{currentCredits}</span>
                </div>
              </div>
              <button className="gc-buy-link" onClick={() => navigate('/buy-credits')}>
                <i className="fas fa-plus"></i> Comprar
              </button>
            </div>
          </div>
        </header>

        {/* ====== TOAST ====== */}
        {toast && (
          <div className={`gc-toast gc-toast-${toast.type}`}>
            <i className={`fas ${toast.type === 'success' ? 'fa-check-circle' : toast.type === 'error' ? 'fa-times-circle' : 'fa-exclamation-triangle'}`}></i>
            <span>{toast.msg}</span>
          </div>
        )}

        {/* ====== MAIN ====== */}
        <main className="gc-main">
          {/* ====== EMPTY STATE — sem créditos ====== */}
          {noCredits ? (
            <div className="gc-empty">
              <div className="gc-empty-icon">
                <i className="fas fa-coins"></i>
              </div>
              <h2 className="gc-empty-title">Sem créditos disponíveis</h2>
              <p className="gc-empty-desc">
                Para gerar códigos de rastreamento você precisa adquirir créditos primeiro.
                <br />Cada código gerado consome 1 crédito.
              </p>
              <button className="gc-empty-cta" onClick={() => navigate('/buy-credits')}>
                <i className="fas fa-cart-shopping"></i> Comprar créditos agora
              </button>
              <button className="gc-empty-back" onClick={() => navigate('/dashboard')}>
                Voltar ao dashboard
              </button>
            </div>
          ) : (
            <>
              {/* ====== RESULTADO (acima do form, com scrollIntoView) ====== */}
              {generatedCode && (
                <div className="gc-result" ref={resultRef}>
                  <div className="gc-result-head">
                    <div className="gc-result-icon">
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <div className="gc-result-titles">
                      <h2 className="gc-result-title">Códigos gerados com sucesso</h2>
                      <p className="gc-result-sub">{success}</p>
                    </div>
                    <button
                      className="gc-result-close"
                      onClick={() => setGeneratedCode(null)}
                      title="Fechar resultado"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>

                  <div className="gc-result-meta">
                    <div className="gc-meta-item">
                      <span className="gc-meta-label">Rota</span>
                      <span className="gc-meta-val">{generatedCode.routeType}</span>
                    </div>
                    <div className="gc-meta-item">
                      <span className="gc-meta-label">Origem</span>
                      <span className="gc-meta-val">{generatedCode.origin || '—'}</span>
                    </div>
                    <div className="gc-meta-item">
                      <span className="gc-meta-label">Quantidade</span>
                      <span className="gc-meta-val">{generatedCode.resultados.length}</span>
                    </div>
                  </div>

                  <div className="gc-codes-scroll">
                    {generatedCode.resultados.map((r, i) => (
                      <div key={r.codigo} className="gc-code-row">
                        <span className="gc-code-num">{i + 1}</span>
                        <span className="gc-code-val">{r.codigo}</span>
                        <span className="gc-code-dest">{r.destino || '—'}</span>
                        <button
                          className="gc-code-copy"
                          onClick={() => copySingle(r.codigo)}
                          title="Copiar este código"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="gc-result-actions">
                    <button className="gc-btn-secondary" onClick={copyAllCodes}>
                      {copied ? <><i className="fas fa-check"></i> Copiado</> : <><i className="fas fa-copy"></i> Copiar todos</>}
                    </button>
                    <button className="gc-btn-secondary" onClick={downloadCSV}>
                      <i className="fas fa-file-csv"></i> Baixar CSV
                    </button>
                    <button className="gc-btn-secondary" onClick={() => navigate('/my-codes')}>
                      <i className="fas fa-folder-open"></i> Ver todos
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <div className="gc-error-banner">
                  <i className="fas fa-exclamation-triangle"></i> {error}
                </div>
              )}

              {/* ====== LAYOUT 2 COLUNAS ====== */}
              <div className="gc-grid">
                {/* ===== FORM ===== */}
                <form className="gc-form" onSubmit={handleSubmit}>
                  {/* honeypot */}
                  <input type="text" name="website" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />

                  {/* ====== ROTA — cards visuais ====== */}
                  <section className="gc-section">
                    <div className="gc-section-head">
                      <div>
                        <h3 className="gc-section-title">
                          <i className="fas fa-route"></i> Tipo de rota
                        </h3>
                        <p className="gc-section-sub">Define as etapas do rastreamento</p>
                      </div>
                    </div>
                    <div className="gc-routes">
                      {ROUTES.map((r) => (
                        <button
                          type="button"
                          key={r.id}
                          className={`gc-route-card ${routeType === r.id ? 'active' : ''}`}
                          onClick={() => setRouteType(r.id)}
                          style={{ '--route-color': r.color }}
                        >
                          <div className="gc-route-flag">{r.flag}</div>
                          <div className="gc-route-info">
                            <div className="gc-route-label">{r.label}</div>
                            <div className="gc-route-title">{r.title}</div>
                            <div className="gc-route-sub">{r.subtitle}</div>
                          </div>
                          {routeType === r.id && (
                            <div className="gc-route-check">
                              <i className="fas fa-check"></i>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </section>

                  {/* ====== ORIGEM ====== */}
                  <section className="gc-section">
                    <div className="gc-section-head">
                      <div>
                        <h3 className="gc-section-title">
                          <i className="fas fa-location-dot"></i> Origem
                          <span className="gc-opt">opcional</span>
                        </h3>
                        <p className="gc-section-sub">Apareceu no histórico do lote</p>
                      </div>
                    </div>
                    <input
                      type="text"
                      className={`gc-input ${errors.origin ? 'has-error' : ''}`}
                      placeholder="Ex: São Paulo, SP"
                      value={origin}
                      maxLength={120}
                      onChange={(e) => {
                        setOrigin(e.target.value);
                        if (errors.origin) setErrors((p) => ({ ...p, origin: '' }));
                      }}
                    />
                    {errors.origin && <p className="gc-msg gc-msg-err">{errors.origin}</p>}
                  </section>

                  {/* ====== DATA DE POSTAGEM ====== */}
                  <section className="gc-section">
                    <div className="gc-section-head">
                      <div>
                        <h3 className="gc-section-title">
                          <i className="fas fa-calendar-day"></i> Data de postagem
                          <span className="gc-opt">opcional</span>
                        </h3>
                        <p className="gc-section-sub">Horário fixo 09:31 · até 30d passado/futuro</p>
                      </div>
                    </div>
                    <div className="gc-date-presets">
                      {[
                        { label: 'Hoje', offset: 0 },
                        { label: 'Ontem', offset: -1 },
                        { label: 'Amanhã', offset: 1 },
                      ].map((p) => (
                        <button
                          type="button"
                          key={p.label}
                          className={`gc-chip ${postDate === dateOffsetISO(p.offset) ? 'active' : ''}`}
                          onClick={() => setDatePreset(p.offset)}
                        >
                          {p.label}
                        </button>
                      ))}
                      {postDate && (
                        <button type="button" className="gc-chip gc-chip-clear" onClick={() => setPostDate('')}>
                          <i className="fas fa-times"></i> Limpar
                        </button>
                      )}
                    </div>
                    <input
                      type="date"
                      className="gc-input"
                      value={postDate}
                      onChange={(e) => setPostDate(e.target.value)}
                      min={dateOffsetISO(-30)}
                      max={dateOffsetISO(30)}
                    />
                    {postDate && (
                      <p className="gc-msg gc-msg-info">
                        <i className="fas fa-info-circle"></i> Postagem em {formatDateBR(postDate)} às 09:31
                      </p>
                    )}
                  </section>

                  {/* ====== DESTINOS ====== */}
                  <section className="gc-section">
                    <div className="gc-section-head">
                      <div>
                        <h3 className="gc-section-title">
                          <i className="fas fa-list-ul"></i> Destinos
                          <span className="gc-opt">opcional</span>
                        </h3>
                        <p className="gc-section-sub">
                          Um por linha · usar &gt;1 ignora o campo "quantidade"
                        </p>
                      </div>
                      <div className="gc-dest-actions">
                        <button type="button" className="gc-mini-btn" onClick={handlePasteDestinations}>
                          <i className={`fas ${pasted ? 'fa-check' : 'fa-paste'}`}></i> {pasted ? 'Colado!' : 'Colar'}
                        </button>
                        {destinationCount > 0 && (
                          <button type="button" className="gc-mini-btn danger" onClick={clearDestinations}>
                            <i className="fas fa-trash"></i> Limpar
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="gc-dest-counter">
                      <span className={`${destinationCount > 900 ? 'danger' : destinationCount > 800 ? 'warn' : ''}`}>
                        <strong>{destinationCount}</strong> / 999
                      </span>
                      <div className="gc-dest-progress">
                        <div
                          className={`gc-dest-progress-bar ${destinationCount > 900 ? 'danger' : destinationCount > 800 ? 'warn' : ''}`}
                          style={{ width: `${destPct}%` }}
                        />
                      </div>
                    </div>

                    <textarea
                      ref={destinationsRef}
                      className={`gc-textarea ${errors.destinations ? 'has-error' : ''}`}
                      placeholder={'Ex:\nSão Paulo, SP\nRio de Janeiro, RJ\nBelo Horizonte, MG'}
                      value={destinations}
                      rows={8}
                      onChange={(e) => handleDestChange(e.target.value)}
                    />
                    {errors.destinations && <p className="gc-msg gc-msg-err">{errors.destinations}</p>}
                    {destinationCount > 900 && !errors.destinations && (
                      <p className="gc-msg gc-msg-warn">
                        <i className="fas fa-exclamation-triangle"></i> Próximo do limite de 999 destinos
                      </p>
                    )}
                  </section>

                  {/* ====== QUANTIDADE ====== */}
                  <section className="gc-section">
                    <div className="gc-section-head">
                      <div>
                        <h3 className="gc-section-title">
                          <i className="fas fa-hashtag"></i> Quantidade de códigos
                          {destinationCount > 1 && <span className="gc-opt locked">auto</span>}
                        </h3>
                        <p className="gc-section-sub">
                          {destinationCount > 1
                            ? `Travada em ${destinationCount} pelo número de destinos`
                            : 'Quantos códigos quer gerar (1 a 100)'}
                        </p>
                      </div>
                    </div>

                    <div className="gc-qty-presets">
                      {QTY_PRESETS.map((n) => (
                        <button
                          type="button"
                          key={n}
                          className={`gc-chip ${parseInt(quantity) === n && destinationCount <= 1 ? 'active' : ''}`}
                          onClick={() => setQuantityPreset(n)}
                          disabled={destinationCount > 1}
                        >
                          {n}
                        </button>
                      ))}
                    </div>

                    <input
                      type="number"
                      min="1"
                      max="100"
                      className={`gc-input ${errors.quantity ? 'has-error' : ''}`}
                      placeholder={destinationCount > 1 ? `${destinationCount} (definido pelos destinos)` : 'Digite ou use os atalhos acima'}
                      value={destinationCount > 1 ? destinationCount : quantity}
                      disabled={destinationCount > 1}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') {
                          setQuantity('');
                        } else {
                          const n = parseInt(v);
                          setQuantity(Number.isFinite(n) ? Math.min(Math.max(n, 1), 100) : '');
                        }
                        if (errors.quantity) setErrors((p) => ({ ...p, quantity: '' }));
                      }}
                    />
                    {errors.quantity && <p className="gc-msg gc-msg-err">{errors.quantity}</p>}
                  </section>

                  {/* ====== SUBMIT MOBILE (sidebar fica abaixo no mobile) ====== */}
                  <div className="gc-submit-mobile">
                    <button
                      type="submit"
                      className="gc-submit"
                      disabled={loading || effectiveQty === 0 || !hasEnoughCredits}
                    >
                      {loading ? (
                        <><i className="fas fa-spinner fa-spin"></i> Gerando...</>
                      ) : (
                        <><i className="fas fa-bolt"></i> Gerar {effectiveQty > 0 ? effectiveQty : ''} código{effectiveQty !== 1 ? 's' : ''}</>
                      )}
                    </button>
                  </div>
                </form>

                {/* ===== SIDEBAR STICKY ===== */}
                <aside className="gc-sidebar">
                  <div className="gc-sidebar-inner">
                    {/* CUSTO / RESUMO */}
                    <div className={`gc-cost ${effectiveQty === 0 ? 'idle' : hasEnoughCredits ? 'ok' : 'err'}`}>
                      <div className="gc-cost-head">
                        <span className="gc-cost-label">Resumo</span>
                        {effectiveQty > 0 && (
                          <span className={`gc-cost-badge ${hasEnoughCredits ? 'ok' : 'err'}`}>
                            <i className={`fas ${hasEnoughCredits ? 'fa-check' : 'fa-times'}`}></i>
                            {hasEnoughCredits ? 'OK' : 'Insuficiente'}
                          </span>
                        )}
                      </div>

                      <div className="gc-cost-rows">
                        <div className="gc-cost-row">
                          <span>Saldo atual</span>
                          <strong>{currentCredits}</strong>
                        </div>
                        <div className="gc-cost-row">
                          <span>Códigos</span>
                          <strong>{effectiveQty || '—'}</strong>
                        </div>
                        <div className="gc-cost-row">
                          <span>Custo</span>
                          <strong className={effectiveQty > 0 ? 'minus' : ''}>
                            {effectiveQty > 0 ? `− ${effectiveQty}` : '—'}
                          </strong>
                        </div>
                        <div className="gc-cost-divider" />
                        <div className="gc-cost-row total">
                          <span>Saldo após</span>
                          <strong className={effectiveQty === 0 ? '' : hasEnoughCredits ? 'ok' : 'err'}>
                            {effectiveQty === 0 ? currentCredits : remainingCredits}
                          </strong>
                        </div>
                      </div>

                      {effectiveQty > 0 && !hasEnoughCredits && (
                        <button type="button" className="gc-cost-buy" onClick={() => navigate('/buy-credits')}>
                          <i className="fas fa-cart-shopping"></i> Faltam {Math.abs(remainingCredits)} — comprar
                        </button>
                      )}
                    </div>

                    {/* PREVIEW DO CÓDIGO */}
                    <div className="gc-preview">
                      <span className="gc-preview-label">Formato</span>
                      <div className="gc-preview-code">{codePreview}</div>
                      <span className="gc-preview-hint">5 letras/números aleatórios + dígito verificador</span>
                    </div>

                    {/* BOTÃO DESKTOP */}
                    <button
                      type="button"
                      className="gc-submit gc-submit-desktop"
                      disabled={loading || effectiveQty === 0 || !hasEnoughCredits}
                      onClick={handleSubmit}
                    >
                      {loading ? (
                        <><i className="fas fa-spinner fa-spin"></i> Gerando...</>
                      ) : (
                        <><i className="fas fa-bolt"></i> Gerar {effectiveQty > 0 ? effectiveQty : ''} código{effectiveQty !== 1 ? 's' : ''}</>
                      )}
                    </button>
                    <p className="gc-shortcut-hint">
                      <kbd>Ctrl</kbd> + <kbd>Enter</kbd> para gerar
                    </p>
                  </div>
                </aside>
              </div>
            </>
          )}
        </main>

        {/* ====== MODAL DE CONFIRMAÇÃO ====== */}
        {showConfirm && (
          <div className="gc-modal-overlay" onClick={() => setShowConfirm(false)}>
            <div className="gc-modal" onClick={(e) => e.stopPropagation()}>
              <div className="gc-modal-icon">
                <i className="fas fa-bolt"></i>
              </div>
              <h3 className="gc-modal-title">Confirmar geração</h3>
              <p className="gc-modal-desc">
                Você está prestes a gerar <strong>{effectiveQty} código{effectiveQty !== 1 ? 's' : ''}</strong> de rota{' '}
                <strong>{routeType}</strong>, consumindo <strong>{effectiveQty} crédito{effectiveQty !== 1 ? 's' : ''}</strong>.
              </p>
              <div className="gc-modal-summary">
                <div className="gc-modal-row">
                  <span>Saldo atual</span>
                  <strong>{currentCredits}</strong>
                </div>
                <div className="gc-modal-row">
                  <span>Custo</span>
                  <strong className="minus">− {effectiveQty}</strong>
                </div>
                <div className="gc-modal-row total">
                  <span>Saldo após</span>
                  <strong className={hasEnoughCredits ? 'ok' : 'err'}>{remainingCredits}</strong>
                </div>
              </div>
              <div className="gc-modal-actions">
                <button type="button" className="gc-modal-cancel" onClick={() => setShowConfirm(false)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="gc-modal-confirm"
                  onClick={doGenerate}
                  disabled={!hasEnoughCredits || loading}
                >
                  <i className="fas fa-check"></i> Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

/* =========================================================================
 * STYLES
 * ========================================================================= */
const styles = `
  .gc *, .gc *::before, .gc *::after { margin: 0; padding: 0; box-sizing: border-box; }
  .gc {
    font-family: 'Lexend', sans-serif; color: #333;
    background: linear-gradient(180deg, #f5f5f7 0%, #ececef 100%);
    min-height: 100vh; -webkit-font-smoothing: antialiased;
  }

  /* ===== HEADER ===== */
  .gc-header {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 100;
  }
  .gc-header-inner {
    max-width: 1280px; margin: 0 auto; padding: 14px 24px;
    display: flex; align-items: center; justify-content: space-between; gap: 16px;
  }
  .gc-header-left { display: flex; align-items: center; gap: 14px; }
  .gc-back-btn {
    width: 40px; height: 40px; border-radius: 10px;
    background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.1);
    color: #f90; cursor: pointer; transition: all .2s;
    display: flex; align-items: center; justify-content: center; font-size: 16px;
  }
  .gc-back-btn:hover { background: rgba(255,153,0,0.15); transform: translateX(-2px); }
  .gc-header-title { font-size: 19px; font-weight: 700; color: #fff; line-height: 1.1; }
  .gc-header-sub { font-size: 12px; color: rgba(255,255,255,0.55); margin-top: 2px; }
  .gc-header-right { display: flex; align-items: center; gap: 12px; }
  .gc-balance-chip {
    display: flex; align-items: center; gap: 12px;
    background: rgba(255,153,0,0.13); border: 1px solid rgba(255,153,0,0.3);
    padding: 8px 16px; border-radius: 12px; color: #fff;
    transition: all .3s;
  }
  .gc-balance-chip i { color: #f90; font-size: 18px; }
  .gc-balance-chip.low { background: rgba(245,158,11,0.18); border-color: rgba(245,158,11,0.5); }
  .gc-balance-chip.empty { background: rgba(229,62,62,0.18); border-color: rgba(229,62,62,0.5); }
  .gc-balance-info { display: flex; flex-direction: column; line-height: 1.1; }
  .gc-balance-label { font-size: 10px; color: rgba(255,255,255,0.6); text-transform: uppercase; letter-spacing: 0.5px; }
  .gc-balance-val { font-size: 17px; font-weight: 700; }
  .gc-buy-link {
    background: linear-gradient(135deg, #f90, #ff6600); color: #fff; border: none;
    padding: 9px 16px; border-radius: 10px; font-weight: 600; font-size: 13px;
    cursor: pointer; display: flex; align-items: center; gap: 6px;
    font-family: 'Lexend', sans-serif; transition: all .2s;
    box-shadow: 0 4px 14px rgba(255,153,0,0.25);
  }
  .gc-buy-link:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(255,153,0,0.4); }

  /* ===== TOAST ===== */
  .gc-toast {
    position: fixed; top: 80px; left: 50%; transform: translateX(-50%);
    background: #1a1a2e; color: #fff; padding: 12px 22px; border-radius: 12px;
    display: flex; align-items: center; gap: 10px; z-index: 9999;
    box-shadow: 0 12px 40px rgba(0,0,0,0.25); font-weight: 500; font-size: 14px;
    animation: gcToastIn .25s ease;
  }
  .gc-toast i { font-size: 18px; }
  .gc-toast-success i { color: #22c55e; }
  .gc-toast-error i { color: #ef4444; }
  .gc-toast-warn i { color: #f59e0b; }
  @keyframes gcToastIn { from { opacity: 0; transform: translate(-50%, -10px); } to { opacity: 1; transform: translate(-50%, 0); } }

  /* ===== MAIN / GRID ===== */
  .gc-main { max-width: 1280px; margin: 0 auto; padding: 28px 24px 60px; }
  .gc-grid {
    display: grid; grid-template-columns: 1fr 360px; gap: 28px; align-items: start;
  }

  /* ===== EMPTY STATE ===== */
  .gc-empty {
    background: #fff; border-radius: 24px; padding: 60px 30px; text-align: center;
    box-shadow: 0 4px 30px rgba(0,0,0,0.06); max-width: 540px; margin: 40px auto;
    border: 1px solid rgba(0,0,0,0.04);
  }
  .gc-empty-icon {
    width: 96px; height: 96px; margin: 0 auto 24px;
    background: linear-gradient(135deg, #fee, #fcc);
    border-radius: 24px; display: flex; align-items: center; justify-content: center;
    box-shadow: 0 10px 30px rgba(239,68,68,0.20);
  }
  .gc-empty-icon i { font-size: 40px; color: #ef4444; }
  .gc-empty-title { font-size: 26px; font-weight: 700; color: #222; margin-bottom: 12px; }
  .gc-empty-desc { font-size: 15px; color: #666; line-height: 1.6; margin-bottom: 30px; }
  .gc-empty-cta {
    background: linear-gradient(135deg, #f90, #ff6600); color: #fff; border: none;
    padding: 16px 32px; border-radius: 14px; font-size: 16px; font-weight: 700;
    cursor: pointer; display: inline-flex; align-items: center; gap: 10px;
    font-family: 'Lexend', sans-serif; box-shadow: 0 8px 24px rgba(255,153,0,0.35);
    transition: all .25s;
  }
  .gc-empty-cta:hover { transform: translateY(-2px); box-shadow: 0 12px 30px rgba(255,153,0,0.45); }
  .gc-empty-back {
    display: block; margin: 18px auto 0; background: none; border: none;
    color: #999; cursor: pointer; font-size: 14px; font-family: 'Lexend', sans-serif;
    text-decoration: underline; padding: 8px;
  }
  .gc-empty-back:hover { color: #555; }

  /* ===== RESULT ===== */
  .gc-result {
    background: linear-gradient(135deg, #ecfdf3, #fff); border-radius: 20px; padding: 26px;
    margin-bottom: 24px; border: 2px solid rgba(34,197,94,0.25);
    box-shadow: 0 10px 30px rgba(34,197,94,0.12);
    animation: gcResultIn .35s ease;
  }
  @keyframes gcResultIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
  .gc-result-head { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
  .gc-result-icon {
    width: 52px; height: 52px; border-radius: 14px;
    background: linear-gradient(135deg, #22c55e, #15803d); color: #fff;
    display: flex; align-items: center; justify-content: center; font-size: 22px;
    box-shadow: 0 6px 16px rgba(34,197,94,0.30);
  }
  .gc-result-titles { flex: 1; }
  .gc-result-title { font-size: 18px; font-weight: 700; color: #15803d; }
  .gc-result-sub { font-size: 13px; color: #4b8763; margin-top: 2px; }
  .gc-result-close {
    width: 32px; height: 32px; border-radius: 8px; background: rgba(255,255,255,0.7);
    border: 1px solid rgba(0,0,0,0.06); color: #777; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
  }
  .gc-result-close:hover { background: #fff; color: #333; }
  .gc-result-meta { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; margin-bottom: 16px; }
  .gc-meta-item { background: rgba(255,255,255,0.7); padding: 10px 14px; border-radius: 10px; }
  .gc-meta-label { display: block; font-size: 11px; color: #777; text-transform: uppercase; letter-spacing: 0.5px; }
  .gc-meta-val { display: block; font-size: 15px; font-weight: 700; color: #222; margin-top: 2px; }

  .gc-codes-scroll {
    background: #fff; border-radius: 14px; max-height: 320px; overflow-y: auto;
    border: 1px solid rgba(0,0,0,0.05); margin-bottom: 16px;
  }
  .gc-code-row {
    display: grid; grid-template-columns: 28px 1fr auto 36px; gap: 12px; align-items: center;
    padding: 12px 16px; border-bottom: 1px solid #f0f0f0;
  }
  .gc-code-row:last-child { border-bottom: none; }
  .gc-code-row:hover { background: #fafafa; }
  .gc-code-num { font-size: 12px; color: #999; font-weight: 500; text-align: center; }
  .gc-code-val { font-family: 'Courier New', monospace; font-weight: 700; color: #f90; font-size: 15px; }
  .gc-code-dest { font-size: 13px; color: #666; text-align: right; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .gc-code-copy {
    width: 32px; height: 32px; border-radius: 8px; background: rgba(255,153,0,0.10);
    border: none; color: #f90; cursor: pointer; transition: all .2s;
    display: flex; align-items: center; justify-content: center;
  }
  .gc-code-copy:hover { background: rgba(255,153,0,0.20); transform: scale(1.05); }

  .gc-result-actions { display: flex; gap: 10px; flex-wrap: wrap; }
  .gc-btn-secondary {
    flex: 1; min-width: 140px; background: #fff; color: #444; border: 1px solid #e0e0e0;
    padding: 12px 16px; border-radius: 10px; font-weight: 600; font-size: 13px;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
    font-family: 'Lexend', sans-serif; transition: all .2s;
  }
  .gc-btn-secondary:hover { border-color: #f90; color: #f90; }

  /* ===== ERROR BANNER ===== */
  .gc-error-banner {
    background: rgba(239,68,68,0.10); border: 1px solid rgba(239,68,68,0.30);
    color: #c53030; padding: 14px 18px; border-radius: 12px; margin-bottom: 20px;
    display: flex; align-items: center; gap: 10px; font-weight: 500;
  }
  .gc-error-banner i { font-size: 18px; }

  /* ===== FORM ===== */
  .gc-form {
    background: #fff; border-radius: 20px; padding: 28px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid rgba(0,0,0,0.04);
  }
  .gc-section { margin-bottom: 26px; }
  .gc-section:last-child { margin-bottom: 0; }
  .gc-section-head {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
    margin-bottom: 12px;
  }
  .gc-section-title {
    font-size: 14px; font-weight: 700; color: #222; display: flex; align-items: center; gap: 8px;
    text-transform: uppercase; letter-spacing: 0.5px;
  }
  .gc-section-title i { color: #f90; font-size: 13px; }
  .gc-section-sub { font-size: 12px; color: #999; margin-top: 2px; }
  .gc-opt {
    font-size: 10px; font-weight: 600; color: #999; background: #f0f0f0;
    padding: 2px 8px; border-radius: 8px; letter-spacing: 0; text-transform: lowercase;
  }
  .gc-opt.locked { background: rgba(255,153,0,0.15); color: #f90; }

  /* ===== ROUTE CARDS ===== */
  .gc-routes { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .gc-route-card {
    position: relative; background: #fafafa; border: 2px solid #ececec; border-radius: 14px;
    padding: 16px 14px; cursor: pointer; transition: all .25s;
    display: flex; flex-direction: column; align-items: center; gap: 8px;
    font-family: 'Lexend', sans-serif; overflow: hidden;
  }
  .gc-route-card:hover { border-color: var(--route-color); background: #fff; transform: translateY(-2px); }
  .gc-route-card.active {
    background: linear-gradient(135deg, color-mix(in srgb, var(--route-color) 12%, #fff), #fff);
    border-color: var(--route-color); box-shadow: 0 6px 20px color-mix(in srgb, var(--route-color) 25%, transparent);
  }
  .gc-route-flag { font-size: 32px; line-height: 1; }
  .gc-route-info { text-align: center; }
  .gc-route-label {
    font-size: 11px; font-weight: 800; color: var(--route-color); letter-spacing: 1px;
  }
  .gc-route-title { font-size: 14px; font-weight: 700; color: #222; margin-top: 2px; }
  .gc-route-sub { font-size: 11px; color: #888; margin-top: 1px; }
  .gc-route-check {
    position: absolute; top: 8px; right: 8px; width: 22px; height: 22px; border-radius: 50%;
    background: var(--route-color); color: #fff; display: flex; align-items: center; justify-content: center;
    font-size: 10px;
  }

  /* ===== INPUTS ===== */
  .gc-input, .gc-textarea {
    width: 100%; padding: 12px 16px; border: 2px solid #e8e8e8; border-radius: 12px;
    font-size: 14px; outline: none; transition: all .2s; font-family: 'Lexend', sans-serif;
    background: #fff; color: #222;
  }
  .gc-textarea { font-family: 'Courier New', monospace; resize: vertical; min-height: 140px; }
  .gc-input:focus, .gc-textarea:focus {
    border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,0.12);
  }
  .gc-input.has-error, .gc-textarea.has-error { border-color: #ef4444; }
  .gc-input:disabled { background: #f5f5f5; color: #999; cursor: not-allowed; }

  .gc-msg { font-size: 12px; margin-top: 8px; display: flex; align-items: center; gap: 6px; }
  .gc-msg-err { color: #ef4444; }
  .gc-msg-warn { color: #f59e0b; }
  .gc-msg-info { color: #3b82f6; }

  /* ===== CHIPS / PRESETS ===== */
  .gc-chip {
    background: #fff; border: 1.5px solid #e8e8e8; padding: 8px 14px; border-radius: 10px;
    font-size: 13px; font-weight: 600; color: #555; cursor: pointer; transition: all .2s;
    font-family: 'Lexend', sans-serif;
  }
  .gc-chip:hover:not(:disabled) { border-color: #f90; color: #f90; }
  .gc-chip.active {
    background: linear-gradient(135deg, #f90, #ff6600); color: #fff; border-color: transparent;
    box-shadow: 0 4px 14px rgba(255,153,0,0.30);
  }
  .gc-chip:disabled { opacity: 0.45; cursor: not-allowed; }
  .gc-chip-clear { color: #ef4444; border-color: rgba(239,68,68,0.20); }
  .gc-chip-clear:hover { background: rgba(239,68,68,0.08); color: #ef4444; border-color: #ef4444; }

  .gc-date-presets, .gc-qty-presets {
    display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap;
  }

  /* ===== DESTINATIONS ===== */
  .gc-dest-actions { display: flex; gap: 6px; }
  .gc-mini-btn {
    background: rgba(255,153,0,0.10); color: #f90; border: 1px solid rgba(255,153,0,0.25);
    padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: 'Lexend', sans-serif; transition: all .2s;
    display: flex; align-items: center; gap: 5px;
  }
  .gc-mini-btn:hover { background: rgba(255,153,0,0.20); }
  .gc-mini-btn.danger { background: rgba(239,68,68,0.08); color: #ef4444; border-color: rgba(239,68,68,0.20); }
  .gc-mini-btn.danger:hover { background: rgba(239,68,68,0.15); }

  .gc-dest-counter {
    display: flex; align-items: center; gap: 12px; margin-bottom: 10px;
    font-size: 12px; color: #666;
  }
  .gc-dest-counter span { white-space: nowrap; }
  .gc-dest-counter strong { color: #222; }
  .gc-dest-counter .warn { color: #f59e0b; }
  .gc-dest-counter .danger { color: #ef4444; }
  .gc-dest-progress {
    flex: 1; height: 6px; background: #f0f0f0; border-radius: 3px; overflow: hidden;
  }
  .gc-dest-progress-bar {
    height: 100%; background: linear-gradient(90deg, #f90, #ff6600); transition: width .3s;
  }
  .gc-dest-progress-bar.warn { background: #f59e0b; }
  .gc-dest-progress-bar.danger { background: #ef4444; }

  /* ===== SIDEBAR ===== */
  .gc-sidebar { position: sticky; top: 90px; }
  .gc-sidebar-inner {
    background: #fff; border-radius: 20px; padding: 22px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid rgba(0,0,0,0.04);
  }

  .gc-cost { padding-bottom: 18px; border-bottom: 1px dashed #eee; margin-bottom: 18px; transition: all .25s; }
  .gc-cost-head {
    display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;
  }
  .gc-cost-label { font-size: 12px; font-weight: 700; color: #222; text-transform: uppercase; letter-spacing: 0.5px; }
  .gc-cost-badge {
    display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px;
    border-radius: 8px; font-size: 11px; font-weight: 700; text-transform: uppercase;
  }
  .gc-cost-badge.ok { background: rgba(34,197,94,0.13); color: #15803d; }
  .gc-cost-badge.err { background: rgba(239,68,68,0.13); color: #c53030; }
  .gc-cost-rows { display: flex; flex-direction: column; gap: 8px; }
  .gc-cost-row { display: flex; align-items: center; justify-content: space-between; font-size: 13px; color: #666; }
  .gc-cost-row strong { color: #222; font-size: 14px; }
  .gc-cost-row strong.minus { color: #ef4444; }
  .gc-cost-row strong.ok { color: #15803d; }
  .gc-cost-row strong.err { color: #c53030; }
  .gc-cost-divider { height: 1px; background: #eee; margin: 4px 0; }
  .gc-cost-row.total strong { font-size: 18px; }
  .gc-cost-buy {
    width: 100%; margin-top: 12px; background: #ef4444; color: #fff; border: none;
    padding: 11px; border-radius: 10px; font-size: 13px; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    font-family: 'Lexend', sans-serif; transition: all .2s;
  }
  .gc-cost-buy:hover { background: #dc2626; transform: translateY(-1px); }

  .gc-preview { text-align: center; padding: 16px 0; border-bottom: 1px dashed #eee; margin-bottom: 18px; }
  .gc-preview-label { display: block; font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
  .gc-preview-code {
    font-family: 'Courier New', monospace; font-size: 22px; font-weight: 800;
    color: #f90; letter-spacing: 2px; background: linear-gradient(135deg, #fff7ed, #fff);
    padding: 10px 14px; border-radius: 10px; border: 1px dashed rgba(255,153,0,0.30);
    display: inline-block;
  }
  .gc-preview-hint { display: block; font-size: 11px; color: #aaa; margin-top: 8px; }

  /* ===== SUBMIT ===== */
  .gc-submit {
    width: 100%; background: linear-gradient(135deg, #f90, #ff6600); color: #fff;
    padding: 15px; border-radius: 12px; font-weight: 700; font-size: 15px; border: none;
    cursor: pointer; transition: all .25s; box-shadow: 0 6px 20px rgba(255,153,0,0.30);
    display: flex; align-items: center; justify-content: center; gap: 10px;
    font-family: 'Lexend', sans-serif;
  }
  .gc-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(255,153,0,0.40); }
  .gc-submit:disabled { background: #ccc; cursor: not-allowed; box-shadow: none; }
  .gc-submit-mobile { display: none; }
  .gc-submit-desktop { display: flex; }
  .gc-shortcut-hint {
    text-align: center; font-size: 11px; color: #aaa; margin-top: 10px;
  }
  .gc-shortcut-hint kbd {
    background: #f0f0f0; padding: 2px 6px; border-radius: 4px;
    font-family: 'Courier New', monospace; font-size: 10px; color: #555;
    border: 1px solid #e0e0e0;
  }

  /* ===== MODAL ===== */
  .gc-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.6);
    display: flex; align-items: center; justify-content: center;
    z-index: 10000; padding: 20px; backdrop-filter: blur(4px);
    animation: gcFadeIn .2s ease;
  }
  @keyframes gcFadeIn { from { opacity: 0; } to { opacity: 1; } }
  .gc-modal {
    background: #fff; border-radius: 20px; max-width: 460px; width: 100%;
    padding: 32px 28px; box-shadow: 0 25px 60px rgba(0,0,0,0.3);
    animation: gcSlideUp .25s ease;
  }
  @keyframes gcSlideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .gc-modal-icon {
    width: 64px; height: 64px; border-radius: 50%;
    background: linear-gradient(135deg, #fff5e6, #ffedd5);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 18px;
  }
  .gc-modal-icon i { font-size: 26px; color: #f90; }
  .gc-modal-title { font-size: 22px; font-weight: 700; color: #222; text-align: center; margin-bottom: 10px; }
  .gc-modal-desc { color: #666; text-align: center; font-size: 14px; margin-bottom: 22px; line-height: 1.5; }
  .gc-modal-desc strong { color: #f90; }
  .gc-modal-summary { background: #fafafa; border-radius: 12px; padding: 16px 18px; margin-bottom: 24px; }
  .gc-modal-row { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; font-size: 14px; color: #555; }
  .gc-modal-row strong { font-size: 16px; color: #222; }
  .gc-modal-row strong.minus { color: #ef4444; }
  .gc-modal-row.total { margin-top: 6px; padding-top: 12px; border-top: 1px solid #e8e8e8; font-size: 15px; font-weight: 600; color: #333; }
  .gc-modal-row strong.ok { color: #15803d; }
  .gc-modal-row strong.err { color: #ef4444; }
  .gc-modal-actions { display: flex; gap: 10px; }
  .gc-modal-cancel, .gc-modal-confirm {
    flex: 1; padding: 14px; border-radius: 12px; font-weight: 600; font-size: 14px;
    border: none; cursor: pointer; transition: all .2s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    font-family: 'Lexend', sans-serif;
  }
  .gc-modal-cancel { background: #f0f0f0; color: #555; }
  .gc-modal-cancel:hover { background: #e5e5e5; }
  .gc-modal-confirm { background: linear-gradient(135deg, #f90, #ff6600); color: #fff; box-shadow: 0 4px 15px rgba(255,153,0,0.30); }
  .gc-modal-confirm:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 22px rgba(255,153,0,0.40); }
  .gc-modal-confirm:disabled { background: #ccc; cursor: not-allowed; box-shadow: none; }

  /* ===== RESPONSIVE ===== */
  /* Monitor estreito / vertical (~1080px): empilha formulário e resultado */
  @media (max-width: 1280px) {
    .gc-grid { grid-template-columns: 1fr; }
    .gc-sidebar { position: static; }
    .gc-submit-mobile { display: flex; margin-top: 20px; }
    .gc-submit-desktop { display: none; }
    .gc-shortcut-hint { display: none; }
  }
  @media (max-width: 720px) {
    .gc-header-inner { padding: 12px 16px; flex-wrap: wrap; gap: 10px; }
    .gc-header-title { font-size: 17px; }
    .gc-header-sub { display: none; }
    .gc-header-right { width: 100%; justify-content: space-between; }
    .gc-balance-chip { padding: 6px 12px; gap: 8px; }
    .gc-balance-val { font-size: 15px; }
    .gc-main { padding: 20px 14px 50px; }
    .gc-form { padding: 20px; border-radius: 16px; }
    .gc-routes { grid-template-columns: 1fr; }
    .gc-route-card { flex-direction: row; padding: 14px; text-align: left; }
    .gc-route-card .gc-route-info { text-align: left; flex: 1; }
    .gc-result-meta { grid-template-columns: 1fr; }
    .gc-code-row { grid-template-columns: 24px 1fr 32px; }
    .gc-code-dest { display: none; }
    .gc-result-actions .gc-btn-secondary { min-width: 0; flex: 1 1 100%; }
    .gc-modal-actions { flex-direction: column-reverse; }
    .gc-empty { padding: 40px 24px; margin: 20px 0; }
    .gc-empty-title { font-size: 22px; }
  }
`;

export default GenerateCode;
